import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Plus, 
  Search, 
  Home, 
  Warehouse, 
  Truck, 
  Box as BoxIcon, 
  Calendar, 
  Package,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  Scale
} from "lucide-react";
import { cn } from "@/lib/utils";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";
import { useToast } from "@/hooks/use-toast";
import UserMenu from "@/components/UserMenu";

interface Box {
  id: string;
  box_number: string;
  name: string;
  location_status: string;
  room: string;
  weight_class: string | null;
  is_fragile: boolean | null;
  thumbnail?: string;
  itemCount?: number;
}

const WEIGHT_LABELS: Record<string, string> = {
  light: "Light",
  medium: "Medium", 
  heavy: "Heavy"
};

const DashboardPreview = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        navigate('/auth');
        return;
      }
      setUser(currentUser);

      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentUser.id)
        .single();
      
      setProfile(profileData);

      // Fetch boxes with new fields
      const { data: boxesData, error: boxesError } = await supabase
        .from('boxes')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (boxesError) throw boxesError;

      // Fetch photos and item counts for each box
      const boxesWithDetails = await Promise.all((boxesData || []).map(async box => {
        // Get photo
        const { data: photos } = await supabase
          .from('photos')
          .select('storage_path')
          .eq('box_id', box.id)
          .order('uploaded_at', { ascending: false })
          .limit(1);

        let thumbnail: string | undefined;
        if (photos && photos.length > 0) {
          const { data } = await supabase.storage
            .from('box-photos')
            .createSignedUrl(photos[0].storage_path, 3600);
          thumbnail = data?.signedUrl;
        }

        // Get item count
        const { count } = await supabase
          .from('items')
          .select('*', { count: 'exact', head: true })
          .eq('box_id', box.id);

        return {
          ...box,
          thumbnail,
          itemCount: count || 0
        };
      }));

      setBoxes(boxesWithDetails);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredBoxes = boxes.filter(box => {
    const matchesSearch = 
      box.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      box.box_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = locationFilter === "all" || box.location_status === locationFilter;
    return matchesSearch && matchesLocation;
  });

  const stats = {
    total: boxes.length,
    home: boxes.filter(b => b.location_status === "home").length,
    stored: boxes.filter(b => b.location_status === "stored").length,
    fragile: boxes.filter(b => b.is_fragile).length
  };

  const getLocationBadge = (status: string) => {
    const variants: Record<string, { label: string; variant: any; icon: any }> = {
      home: { label: "At Home", variant: "secondary", icon: Home },
      stored: { label: "Stored", variant: "default", icon: Warehouse },
      in_transit: { label: "In Transit", variant: "outline", icon: Truck },
    };
    const config = variants[status] || variants.home;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex flex-col items-center justify-center p-4">
        <img src={endlessStorageLogo} alt="Endless Storage" className="h-16 mb-6 opacity-80" />
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
        <p className="text-muted-foreground text-sm">Loading your inventory...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity"
            >
              <img src={endlessStorageLogo} alt="Endless Storage" className="h-12" />
            </button>
            <UserMenu 
              userEmail={user?.email || ""} 
              userName={profile?.full_name}
            />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Empty State */}
        {boxes.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="h-12 w-12 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Start Your Inventory</h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Log your first box to start tracking your belongings. Snap a photo and we'll help catalog everything inside.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto">
              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate('/supplies')}
              >
                <CardContent className="pt-6 text-center">
                  <Package className="h-10 w-10 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-1">Get Free Storage Kit</h3>
                  <p className="text-sm text-muted-foreground">
                    5 boxes, labels, and supplies
                  </p>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate('/box/log')}
              >
                <CardContent className="pt-6 text-center">
                  <Sparkles className="h-10 w-10 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-1">Log Your First Box</h3>
                  <p className="text-sm text-muted-foreground">
                    Already have boxes? Start logging
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Bar */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="p-4">
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-muted-foreground">Total Boxes</div>
              </Card>
              <Card className="p-4">
                <div className="text-2xl font-bold">{stats.home}</div>
                <div className="text-sm text-muted-foreground">At Home</div>
              </Card>
              <Card className="p-4">
                <div className="text-2xl font-bold">{stats.stored}</div>
                <div className="text-sm text-muted-foreground">Stored</div>
              </Card>
              <Card className="p-4">
                <div className="text-2xl font-bold text-orange-500">{stats.fragile}</div>
                <div className="text-sm text-muted-foreground">Fragile</div>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search boxes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={() => navigate('/box/log')}>
                <Plus className="h-4 w-4 mr-2" />
                Log New Box
              </Button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {[
                { value: "all", label: "All" },
                { value: "home", label: "At Home", icon: Home },
                { value: "stored", label: "Stored", icon: Warehouse },
              ].map((filter) => (
                <Button
                  key={filter.value}
                  variant={locationFilter === filter.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLocationFilter(filter.value)}
                  className="gap-2"
                >
                  {filter.icon && <filter.icon className="h-4 w-4" />}
                  {filter.label}
                </Button>
              ))}
            </div>

            {/* Box Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBoxes.map((box) => (
                <Card 
                  key={box.id}
                  className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/box/${box.id}`)}
                >
                  {box.thumbnail ? (
                    <div className="aspect-video relative bg-muted">
                      <img
                        src={box.thumbnail}
                        alt={box.name}
                        className="w-full h-full object-cover"
                      />
                      {box.is_fragile && (
                        <div className="absolute top-2 right-2">
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Fragile
                          </Badge>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-video bg-muted flex items-center justify-center relative">
                      <BoxIcon className="h-12 w-12 text-muted-foreground/50" />
                      {box.is_fragile && (
                        <div className="absolute top-2 right-2">
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Fragile
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold">{box.name}</h3>
                        <p className="text-sm text-muted-foreground font-mono">
                          {box.box_number}
                        </p>
                      </div>
                      {getLocationBadge(box.location_status)}
                    </div>
                    
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>{box.room}</span>
                      {box.itemCount > 0 && (
                        <>
                          <span>•</span>
                          <span>{box.itemCount} items</span>
                        </>
                      )}
                      {box.weight_class && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Scale className="h-3 w-3" />
                            {WEIGHT_LABELS[box.weight_class]}
                          </span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredBoxes.length === 0 && searchQuery && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No boxes found matching "{searchQuery}"</p>
              </div>
            )}

            {/* Ready to Store CTA */}
            {stats.home > 0 && (
              <Card className="mt-8 border-primary/20 bg-primary/5">
                <CardContent className="flex items-center justify-between p-6">
                  <div>
                    <h3 className="font-semibold mb-1">Ready to store your boxes?</h3>
                    <p className="text-sm text-muted-foreground">
                      Schedule your first free pickup for up to {stats.home} boxes
                    </p>
                  </div>
                  <Button onClick={() => navigate('/schedule-pickup')}>
                    Schedule Pickup
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPreview;
